// templateText.ts
// Small reads of a template's raw merge text for the gallery's cards and preview.

/** The questionnaire keywords that start a line, longest first so `FOR EACH` wins over `FOR`. */
const LEADING_KEYWORD =
  /^(\s*)(FOR EACH|SELECT MULTIPLE FROM|SELECT ONE FROM|FILL IN TEXT|FILL IN NUMBER|RANGE LIST|LIST EXCEPTIONS|EXCEPTIONS|ELSE IF|HIDDEN|RECODE|BEGIN|END|ELSE|DATA|LIST|SECTION|NOTE|TEXT|IF|Q)\b/

/**
 * The feature tags of a template, read from its merge text: `FOR EACH`,
 * `RECODE`, `routing` (any IF) and `HIDDEN`, in that order.
 *
 * @param merge - The template's raw merge text
 * @returns The tags that apply
 */
export const templateTags = (merge: string): string[] => {
  const tags: string[] = []
  if (/^\s*FOR EACH\b/m.test(merge)) tags.push("FOR EACH")
  if (/\bRECODE\b/.test(merge)) tags.push("RECODE")
  if (/^\s*(ELSE\s+)?IF\b/m.test(merge)) tags.push("routing")
  if (/^\s*HIDDEN\b/m.test(merge)) tags.push("HIDDEN")
  return tags
}

/** A line that only opens or closes an optional part (`{{#X}}`, `{{/X}}`). */
const PART_MARKER = /^\s*\{\{[#/]\w+\}\}\s*$/

/**
 * The merge text as the user should read it, without the engine's
 * internals: the format id inside a placeholder (`{{EXCLUDED[100]}}` reads
 * `{{EXCLUDED}}`) and the lines that only open or close an optional part
 * (`{{#ASK_REGION}}`, `{{/ASK_REGION}}`).
 *
 * @param merge - The template's raw merge text
 * @returns The text for the card peek and the preview pane
 */
export const readableMerge = (merge: string): string =>
  merge
    .split(/\r?\n/)
    .filter((line) => !PART_MARKER.test(line))
    .join("\n")
    .replace(/\{\{(\w+)\[\d+\]\}\}/g, "{{$1}}")

/**
 * The card's peek: the first 4 non-empty lines of the readable merge text.
 *
 * @param merge - The template's raw merge text
 * @returns Up to 4 lines
 */
export const templatePeek = (merge: string): string[] =>
  readableMerge(merge)
    .split(/\r?\n/)
    .filter((line) => line.trim() !== "")
    .slice(0, 4)

/**
 * Splits a line into its leading keyword (if any) and the rest, for colouring.
 *
 * @param line - One line of merge text
 * @returns The indentation, the keyword ("" when none) and the rest
 */
export const splitKeyword = (
  line: string
): { indent: string; keyword: string; rest: string } => {
  const m = LEADING_KEYWORD.exec(line)
  if (!m) return { indent: "", keyword: "", rest: line }
  return { indent: m[1], keyword: m[2], rest: line.slice(m[0].length) }
}

/**
 * A template input name as words, for "Asks for": lower case, underscores as
 * spaces, first letter capitalised (`SUBJECT_TEXT` reads "Subject text").
 *
 * @param label - The input's name in the template
 * @returns The name as words
 */
export const inputLabelAsWords = (label: string): string => {
  const words = label.toLowerCase().replace(/_+/g, " ").trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}
